import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import api from '@/services/api-client'

interface TaskAssignment {
  id: number
  task_id: number
  title: string
  description?: string
  assigned_to: number
  assigned_by: number
  assigned_to_department_id?: number
  assigned_to_user_name?: string
  assigned_to_department_name?: string
  due_date?: string | null
  assignment_algorithm?: string
  assignment_reason?: string
  estimated_completion?: string
  actual_completion?: string
  // Nota: el backend puede devolver 'assigned' (asignada pero no aceptada) y 'cancelled'
  status: 'pending' | 'scheduled' | 'assigned' | 'accepted' | 'in_progress' | 'completed' | 'rejected' | 'cancelled'
  // Ampliado para aceptar valores del backend: 'low','normal','high','urgent','critical'
  priority: 'low' | 'normal' | 'medium' | 'high' | 'urgent' | 'critical'
  workload_score?: number
  expertise_match?: number
  created_at: string
  updated_at: string
}

interface Delegation {
  id: number
  original_assignment_id: number
  delegated_from: number
  delegated_to: number
  delegation_reason: string
  delegation_type: 'manual' | 'automatic' | 'escalation'
  status: 'pending' | 'accepted' | 'rejected'
  created_at: string
  accepted_at?: string
}

interface AssignmentMetrics {
  total_assignments?: number
  completed_assignments?: number
  pending_assignments?: number
  average_completion_time?: number
  success_rate?: number
  user_workload?: Record<string, number>
  algorithm_performance?: Record<string, number>
}

/** P13-V7B (refutation): counts the SERVER stated for the whole list result (see `statedCounts`). */
interface StatedAssignmentCounts {
  total: number
  byStatus: Record<string, number>
  byPriority: Record<string, number>
}

const statedCount = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * The `statistics` block of `GET /task-assignments`. `by_status` / `by_priority` are GROUP BY results
 * over the same where clause as `total_tasks`: a status missing from them has no rows, so it is a real
 * zero. Without a stated `total_tasks` the block proves nothing and is rejected whole.
 */
function normalizeListStatistics(block: any): StatedAssignmentCounts | null {
  if (!block || typeof block !== 'object') return null
  const total = statedCount(block.total_tasks)
  if (total === null) return null
  const group = (rows: unknown, key: string): Record<string, number> => {
    const out: Record<string, number> = {}
    for (const row of Array.isArray(rows) ? rows : []) {
      const name = (row as any)?.[key]
      const count = statedCount((row as any)?.count)
      if (typeof name === 'string' && count !== null) out[name] = (out[name] || 0) + count
    }
    return out
  }
  return { total, byStatus: group(block.by_status, 'status'), byPriority: group(block.by_priority, 'priority') }
}

// Valores UI mapeados a backend: balanced, expertise, workload, priority, geographic
type AssignmentAlgorithm = 'balanced' | 'least_loaded' | 'expertise_based' | 'departmental' | 'priority_based'

function mapAlgorithmToBackend(alg: AssignmentAlgorithm): 'balanced' | 'expertise' | 'workload' | 'priority' | 'geographic' {
  switch (alg) {
    case 'balanced': return 'balanced'
    case 'least_loaded': return 'workload'
    case 'expertise_based': return 'expertise'
    case 'departmental': return 'geographic' // decisión: aproximar "departamental" a distribución geográfica
    case 'priority_based': return 'priority'
  }
}

export const useAssignmentsStore = defineStore('assignments', () => {
  // 🔄 Estado Reactivo
  const assignments = ref<TaskAssignment[]>([])
  const delegations = ref<Delegation[]>([])
  const metrics = ref<AssignmentMetrics | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastUpdate = ref<Date | null>(null)
  const CACHE_TTL_MS = 60_000 // 1 min
  const autoAssignmentEnabled = ref(true)
  const defaultAlgorithm = ref<AssignmentAlgorithm>('balanced')

  /**
   * P13-V7B (refutation) — WHAT THE LIST READ STATES ABOUT THE WHOLE RESULT.
   *
   * `GET /task-assignments` is paginated (TaskAssignmentController.getAll: `limit = 20` by default)
   * and answers `{ success, data: rows, pagination: { total_items, … }, statistics: { total_tasks,
   * by_status: [{ status, count }], by_priority: [{ priority, count }], overdue_tasks } }` — the
   * statistics computed over the SAME where clause as the rows, not over the page. The read went
   * through the default unwrap, which keeps `data` and drops everything beside it, so the KPI cards
   * counted the 20 rows of one page as the totals, and a failed read left them at 0 / 0 %.
   * The list is now read with `skipUnwrap`, and the server's own figures are kept beside the rows:
   * `null` means "not stated" (unread, failed, or the server sent none), never zero.
   */
  const listTotal = ref<number | null>(null)
  const listStatistics = ref<StatedAssignmentCounts | null>(null)
  const listReadFailed = ref(false)

  // 📊 Computed Properties
  const totalAssignments = computed(() => assignments.value.length)
  const pendingAssignments = computed(() => 
    assignments.value.filter(a => a.status === 'pending')
  )
  const activeAssignments = computed(() => 
    assignments.value.filter(a => ['accepted', 'in_progress'].includes(a.status))
  )
  const completedAssignments = computed(() => 
    assignments.value.filter(a => a.status === 'completed')
  )
  const criticalAssignments = computed(() => 
    assignments.value.filter(a => a.priority === 'critical')
  )

  const assignmentsByStatus = computed(() => {
    // Incluir 'assigned' para reflejar el estado que envía el backend
    const statusCount: Record<string, number> = {
      pending: 0,
  scheduled: 0,
      assigned: 0,
      accepted: 0,
      in_progress: 0,
      completed: 0,
      rejected: 0,
      cancelled: 0
    }
    assignments.value.forEach(assignment => {
      const key = assignment.status || 'pending'
      // Contabiliza estados desconocidos sin romper
      if (!(key in statusCount)) statusCount[key] = 0
      statusCount[key] += 1
    })
    return statusCount as {
      pending: number
  scheduled: number
      assigned: number
      accepted: number
      in_progress: number
      completed: number
      rejected: number
      cancelled: number
    }
  })

  const assignmentsByUser = computed(() => {
    const userAssignments: Record<number, TaskAssignment[]> = {}
    assignments.value.forEach(assignment => {
      if (!userAssignments[assignment.assigned_to]) {
        userAssignments[assignment.assigned_to] = []
      }
      userAssignments[assignment.assigned_to].push(assignment)
    })
    return userAssignments
  })

  const userWorkload = computed(() => {
    const workload: Record<number, number> = {}
    assignments.value.forEach(assignment => {
  // Carga activa: excluir 'scheduled' (aún no entra en vigor)
  if (['pending', 'assigned', 'accepted', 'in_progress'].includes(assignment.status)) {
        const uid = assignment.assigned_to
        if (uid != null) {
          workload[uid] = (workload[uid] || 0) + 1
        }
      }
    })
    return workload
  })

  const pendingDelegations = computed(() =>
    delegations.value.filter(d => d.status === 'pending')
  )

  /**
   * P13-V7B (refutation) — the ONE source the assignment KPI cards may publish from.
   *  · the server's `statistics` block, when the last list read carried one;
   *  · otherwise the loaded rows, but only when the server's `total_items` proves they ARE the whole
   *    result (one page holding everything);
   *  · otherwise, and after a failed read, `null`: the cards say "Sin datos".
   */
  const statedCounts = computed<StatedAssignmentCounts | null>(() => {
    if (listReadFailed.value) return null
    if (listStatistics.value) return listStatistics.value
    if (listTotal.value !== null && listTotal.value === assignments.value.length) {
      const byStatus: Record<string, number> = {}
      const byPriority: Record<string, number> = {}
      for (const a of assignments.value) {
        byStatus[a.status] = (byStatus[a.status] || 0) + 1
        byPriority[a.priority] = (byPriority[a.priority] || 0) + 1
      }
      return { total: assignments.value.length, byStatus, byPriority }
    }
    return null
  })

  // Normalizador de asignaciones (mapea campos backend -> UI)
  function normalizeAssignment(input: any): TaskAssignment {
    return {
      id: input.id,
      task_id: input.task_id ?? input.id, // fallback conservador
  title: input.title ?? `Asignación #${input.id}`,
  description: input.description,
      assigned_to: input.assigned_to ?? input.assigned_to_user_id ?? null,
      assigned_by: input.assigned_by ?? input.assigned_by_user_id ?? null,
  assigned_to_department_id: input.assigned_to_department_id ?? input.AssignedToDepartment?.id ?? null,
  assigned_to_user_name: input.AssignedToUser?.name || input.assigned_to_user_name,
  assigned_to_department_name: input.AssignedToDepartment?.name || input.assigned_to_department_name,
  due_date: input.due_date ?? null,
      assignment_algorithm: input.assignment_algorithm,
      assignment_reason: input.assignment_reason,
      estimated_completion: input.estimated_completion,
      actual_completion: input.actual_completion,
      status: input.status ?? 'pending',
      priority: input.priority ?? 'medium',
      workload_score: input.workload_score,
      expertise_match: input.expertise_match,
      created_at: input.created_at ?? new Date().toISOString(),
      updated_at: input.updated_at ?? input.created_at ?? new Date().toISOString(),
    } as TaskAssignment
  }

  // 🌐 API Actions
  const fetchAssignments = async (params?: any, options: { force?: boolean; ttlMs?: number } = {}): Promise<void> => {
    const ttl = options.ttlMs ?? CACHE_TTL_MS
    if (!options.force && lastUpdate.value && Date.now() - lastUpdate.value.getTime() < ttl && assignments.value.length) {
      return
    }
    loading.value = true
    error.value = null
    try {
  // P13-V7B (refutation): `skipUnwrap` keeps `pagination` and `statistics` beside the rows.
  const response = await api.get('/task-assignments', ({ params, skipUnwrap: true, ...(options.force ? { disableCache: true } : {}) } as any))
      const payload = response.data
      const data = payload?.data ?? payload
      const rows = Array.isArray(data) ? data : (Array.isArray(data?.rows) ? data.rows : [])
      assignments.value = rows.map(normalizeAssignment)
      listTotal.value = statedCount(payload?.pagination?.total_items)
      listStatistics.value = normalizeListStatistics(payload?.statistics)
      listReadFailed.value = false
      lastUpdate.value = new Date()
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error loading assignments'
      // P13-V7B (refutation): a failed read states nothing about the totals.
      listTotal.value = null
      listStatistics.value = null
      listReadFailed.value = true
      console.error('Assignments fetch error:', err)
    } finally {
      loading.value = false
    }
  }

  // No existe endpoint /api/delegations en backend actual; usar getById/history bajo demanda
  const fetchDelegations = async (_options: { force?: boolean; ttlMs?: number } = {}): Promise<void> => {
    console.warn('fetchDelegations: no disponible; usa fetchDelegationChain(assignmentId)')
  }

  // Obtener cadena de delegación desde detalle de la tarea
  const fetchDelegationChain = async (assignmentId: number): Promise<any | null> => {
    loading.value = true
    try {
  const response = await api.get(`/task-assignments/${assignmentId}`)
      const payload = response.data
      const data = payload?.data || payload
      return data?.delegation_chain ?? null
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error loading delegation chain'
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchMetrics = async (options: { force?: boolean; ttlMs?: number } = {}): Promise<void> => {
    const ttl = options.ttlMs ?? CACHE_TTL_MS
    if (!options.force && lastUpdate.value && Date.now() - lastUpdate.value.getTime() < ttl && metrics.value) {
      return
    }
    loading.value = true
    try {
      // Backend expone /api/task-assignments/statistics con { success, data: { general_statistics, algorithm_performance, ... } }
  const response = await api.get('/task-assignments/statistics', (options.force ? { disableCache: true } : ({} as any)))
      const payload = response.data
      const data = payload?.data || payload
      const normalized: AssignmentMetrics = {
        // Campos opcionales por si el backend cambia estructura
        total_assignments: data?.general_statistics?.total_tasks ?? data?.total_assignments,
        completed_assignments: data?.general_statistics?.by_status?.find?.((s: any) => s.status === 'completed')?.count ?? data?.completed_assignments,
        pending_assignments: data?.general_statistics?.by_status?.find?.((s: any) => s.status === 'pending')?.count ?? data?.pending_assignments,
        average_completion_time: data?.efficiency_report?.average_completion_time ?? data?.average_completion_time,
        success_rate: typeof data?.efficiency_report?.success_rate === 'number' ? data.efficiency_report.success_rate : data?.success_rate,
        user_workload: data?.user_workload || {},
        algorithm_performance: data?.algorithm_performance || {}
      }
      metrics.value = normalized
      lastUpdate.value = new Date()
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error loading metrics'
      console.error('Metrics fetch error:', err)
    } finally {
      loading.value = false
    }
  }

  // 📈 Estadísticas filtradas por departamento (para vistas de Jefe de Departamento)
  const fetchMetricsForDepartment = async (departmentId: number, options: { force?: boolean; ttlMs?: number } = {}): Promise<void> => {
    const ttl = options.ttlMs ?? CACHE_TTL_MS
    if (!options.force && lastUpdate.value && Date.now() - lastUpdate.value.getTime() < ttl && metrics.value) {
      return
    }
    loading.value = true
    try {
      const params = { department_id: departmentId }
      const response = await api.get('/task-assignments/statistics', { params, ...(options.force ? { disableCache: true } as any : {}) } as any)
      const payload = response.data
      const data = payload?.data || payload
      const normalized: AssignmentMetrics = {
        total_assignments: data?.general_statistics?.total_tasks ?? data?.total_assignments,
        completed_assignments: data?.general_statistics?.by_status?.find?.((s: any) => s.status === 'completed')?.count ?? data?.completed_assignments,
        pending_assignments: data?.general_statistics?.by_status?.find?.((s: any) => s.status === 'pending')?.count ?? data?.pending_assignments,
        average_completion_time: data?.efficiency_report?.average_completion_time ?? data?.average_completion_time,
        success_rate: typeof data?.efficiency_report?.success_rate === 'number' ? data.efficiency_report.success_rate : data?.success_rate,
        user_workload: data?.user_workload || {},
        algorithm_performance: data?.algorithm_performance || {}
      }
      metrics.value = normalized
      lastUpdate.value = new Date()
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error loading metrics (department)'
      console.error('Metrics (department) fetch error:', err)
    } finally {
      loading.value = false
    }
  }

  // 🎯 Assignment Operations
  const createTask = async (payload: any): Promise<TaskAssignment> => {
    loading.value = true
    try {
  const response = await api.post('/task-assignments', payload)
      const body = response.data
      const assignment = normalizeAssignment(body?.data ?? body)
      assignments.value.unshift(assignment)
      lastUpdate.value = null
      return assignment
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error creating task'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Mantener createAssignment (compat) llamando a createTask
  const createAssignment = async (assignmentData: {
    task_id: number
    assigned_to?: number
    algorithm?: AssignmentAlgorithm
    auto_assign?: boolean
  }): Promise<TaskAssignment> => {
    loading.value = true
    try {
  const response = await api.post('/task-assignments', {
        ...assignmentData,
        auto_assign: assignmentData.auto_assign ?? autoAssignmentEnabled.value,
        // Alinear nombre de campo con backend
        assignment_algorithm: assignmentData.algorithm 
          ? mapAlgorithmToBackend(assignmentData.algorithm)
          : mapAlgorithmToBackend(defaultAlgorithm.value)
      })
      const payload = response.data
      const newAssignment = normalizeAssignment(payload?.data ?? payload)
      assignments.value.unshift(newAssignment)
      // Invalida cache para próximas lecturas
      lastUpdate.value = null
      return newAssignment
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error creating assignment'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * P13-V7B — auto-assign THE TASK THAT WAS CHOSEN.
   *
   * This action received a task id and did not use it. It posted a NEW task to
   * `POST /task-assignments` titled `Task <id>`, described "Auto-assigned from UI", typed
   * `incident_response` and bound to the literal `administration_id: 1`: every click on "Auto-Asignar"
   * left the selected task untouched and minted an unrelated one in administration 1, whatever
   * administration the task — or the actor — belonged to.
   *
   * The capability the control names exists: `POST /api/task-assignments/:id/auto-assign` runs the
   * assignment engine over the EXISTING task, inside that task's own administration scope, and
   * answers `{ success, data: <task> }`. It reads `algorithm` and nothing else, so no constraint is
   * sent: a field the engine ignores would be a promise the screen cannot keep.
   */
  const autoAssignTask = async (
    taskId: number,
    algorithm: AssignmentAlgorithm = 'balanced'
  ): Promise<TaskAssignment> => {
    if (!Number.isInteger(taskId) || taskId <= 0) {
      throw new Error('Debes seleccionar una tarea para auto-asignar.')
    }
    loading.value = true
  try {
  const response = await api.post(`/task-assignments/${taskId}/auto-assign`, {
        algorithm: mapAlgorithmToBackend(algorithm)
      })
  const body = response.data
  const assignment = normalizeAssignment(body?.data ?? body)
  const index = assignments.value.findIndex(a => a.id === assignment.id)
  if (index !== -1) assignments.value[index] = assignment
  lastUpdate.value = null
      return assignment
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error auto-assigning task'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateAssignment = async (
    id: number, 
    updateData: Partial<TaskAssignment>
  ): Promise<TaskAssignment> => {
    loading.value = true
    try {
  const response = await api.put(`/task-assignments/${id}`, updateData)
      const payload = response.data
      const updatedAssignment = normalizeAssignment(payload?.data ?? payload)
      const index = assignments.value.findIndex(a => a.id === id)
      if (index !== -1) {
        assignments.value[index] = updatedAssignment
      }
  lastUpdate.value = null
      return updatedAssignment
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error updating assignment'
      throw err
    } finally {
      loading.value = false
    }
  }

  const acceptAssignment = async (id: number): Promise<TaskAssignment> => {
    return updateAssignment(id, { status: 'accepted' })
  }

  const startAssignment = async (id: number): Promise<TaskAssignment> => {
    return updateAssignment(id, { status: 'in_progress' })
  }

  const completeAssignment = async (id: number): Promise<TaskAssignment> => {
    return updateAssignment(id, { 
      status: 'completed',
      actual_completion: new Date().toISOString()
    })
  }

  const rejectAssignment = async (id: number, reason?: string): Promise<TaskAssignment> => {
    return updateAssignment(id, { 
      status: 'rejected',
      assignment_reason: reason
    })
  }

  const deleteAssignment = async (id: number, reason?: string): Promise<void> => {
    loading.value = true
    try {
  await api.delete(`/task-assignments/${id}` as any, { data: reason ? { reason } : undefined } as any)
      const idx = assignments.value.findIndex(a => a.id === id)
      if (idx !== -1) assignments.value.splice(idx, 1)
      lastUpdate.value = null
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error deleting assignment'
      throw err
    } finally {
      loading.value = false
    }
  }

  // 🔄 Delegation Operations
  const delegateAssignment = async (
    assignmentId: number,
    toUserId: number,
    reason: string,
    notifyStakeholders: boolean = true
  ): Promise<{ task: TaskAssignment; delegation_chain?: any } | null> => {
    loading.value = true
    try {
  const response = await api.post(`/task-assignments/${assignmentId}/delegate`, {
        to_user_id: toUserId,
        reason,
        notify_stakeholders: notifyStakeholders
      })
      const payload = response.data
      const data = payload?.data || payload
      const updatedTask: TaskAssignment = normalizeAssignment(data)
      // actualizar en memoria
      const idx = assignments.value.findIndex(a => a.id === assignmentId)
      if (idx !== -1) assignments.value[idx] = updatedTask
      lastUpdate.value = null
      return { task: updatedTask, delegation_chain: payload?.delegation_chain }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error delegating assignment'
      throw err
    } finally {
      loading.value = false
    }
  }

  const autoDelegateAssignment = async (assignmentId: number, payload: { reason?: string } = {}): Promise<TaskAssignment> => {
    loading.value = true
    try {
  const response = await api.post(`/task-assignments/${assignmentId}/auto-delegate`, payload)
      const data = response.data?.data || response.data
      const updated = normalizeAssignment(data)
      const idx = assignments.value.findIndex(a => a.id === assignmentId)
      if (idx !== -1) assignments.value[idx] = updated
      lastUpdate.value = null
      return updated
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error en auto-delegación'
      throw err
    } finally {
      loading.value = false
    }
  }

  const acceptDelegation = async (_delegationId: number): Promise<never> => {
    throw new Error('Aceptar delegación no disponible en backend actual')
  }

  const rejectDelegation = async (_delegationId: number): Promise<never> => {
    throw new Error('Rechazar delegación no disponible en backend actual')
  }

  // 🔍 Utility Functions
  const getAssignmentById = (id: number): TaskAssignment | undefined => {
    return assignments.value.find(a => a.id === id)
  }

  const getAssignmentsByUser = (userId: number): TaskAssignment[] => {
    return assignments.value.filter(a => a.assigned_to === userId)
  }

  const getDelegationById = (id: number): Delegation | undefined => {
    return delegations.value.find(d => d.id === id)
  }

  const getUserWorkload = (userId: number): number => {
    return userWorkload.value[userId] || 0
  }

  const setDefaultAlgorithm = (algorithm: AssignmentAlgorithm): void => {
    defaultAlgorithm.value = algorithm
  }

  const toggleAutoAssignment = (): void => {
    autoAssignmentEnabled.value = !autoAssignmentEnabled.value
  }

  const resetState = (): void => {
    assignments.value = []
    delegations.value = []
    metrics.value = null
    loading.value = false
    error.value = null
    lastUpdate.value = null
    listTotal.value = null
    listStatistics.value = null
    listReadFailed.value = false
  }

  // ⚡ Helpers de optimización
  const invalidateCache = (): void => {
    lastUpdate.value = null
  }

  // 🔄 WebSocket Integration Ready
  const handleRealtimeUpdate = (data: any): void => {
    switch (data.type) {
      case 'assignment_created':
        assignments.value.unshift(data.assignment)
        break
      case 'assignment_updated':
        const assignmentIndex = assignments.value.findIndex(a => a.id === data.assignment.id)
        if (assignmentIndex !== -1) {
          assignments.value[assignmentIndex] = data.assignment
        }
        break
      case 'assignment_accepted':
      case 'assignment_completed':
      case 'assignment_rejected':
        const statusIndex = assignments.value.findIndex(a => a.id === data.assignment.id)
        if (statusIndex !== -1) {
          assignments.value[statusIndex] = data.assignment
        }
        break
      case 'delegation_created':
        delegations.value.unshift(data.delegation)
        break
      case 'delegation_accepted':
      case 'delegation_rejected':
        const delegationIndex = delegations.value.findIndex(d => d.id === data.delegation.id)
        if (delegationIndex !== -1) {
          delegations.value[delegationIndex] = data.delegation
        }
        break
      case 'metrics_updated':
        metrics.value = data.metrics
        break
    }
    lastUpdate.value = new Date()
  }

  return {
    // State
    assignments: readonly(assignments),
    delegations: readonly(delegations),
    metrics: readonly(metrics),
    loading: readonly(loading),
    error: readonly(error),
    lastUpdate: readonly(lastUpdate),
    autoAssignmentEnabled: readonly(autoAssignmentEnabled),
    defaultAlgorithm: readonly(defaultAlgorithm),
    // P13-V7B (refutation): what the server stated about the whole list result
    listTotal: readonly(listTotal),
    listStatistics: readonly(listStatistics),
    listReadFailed: readonly(listReadFailed),
    statedCounts,

    // Computed
    totalAssignments,
    pendingAssignments,
    activeAssignments,
    completedAssignments,
    criticalAssignments,
    assignmentsByStatus,
    assignmentsByUser,
    userWorkload,
    pendingDelegations,
    
    // Actions
    fetchAssignments,
    fetchDelegations,
    fetchMetrics,
  fetchMetricsForDepartment,
    createTask,
    createAssignment,
    autoAssignTask,
    updateAssignment,
    acceptAssignment,
    completeAssignment,
    rejectAssignment,
  deleteAssignment,
    delegateAssignment,
    autoDelegateAssignment,
    acceptDelegation,
    rejectDelegation,
    getAssignmentById,
    getAssignmentsByUser,
    getDelegationById,
    getUserWorkload,
    setDefaultAlgorithm,
    toggleAutoAssignment,
    resetState,
  invalidateCache,
  fetchDelegationChain,
    handleRealtimeUpdate
  }
})
